/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { makeStyles } from '@material-ui/core';
import React, { useCallback } from 'react';
import { Status } from 'reapop';
import clsx from 'clsx';
import get from 'lodash/get';
import IconButton from '#web-components/components/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Button, { ButtonVariants } from '#web-components/components/Button/Button';
import Typography from '#web-components/components/Typography';
import styles from './FlashMessage.styles';
import FlashMessageIcon from './components/FlashMessageIcon';

export enum ViewType {
  notification,
  plain,
}

export interface FlashMessageProps {
  status: Status | 'service';
  title?: string;
  size?: 'large' | 'medium' | 'small';
  message?: string | React.ReactNode;
  viewType?: ViewType;
  hideIcon?: boolean;
  onClose?: () => void;
  hasButton?: boolean;
  buttonTitle?: string;
  buttonHandler?: () => void;
  customIcon?: React.ElementType;
  className?: string;
  onClick?: () => void;
  xpathConfig?: {
    messageBlock?: string,
    titleBlock?: string,
  };
}

const useStyles = makeStyles(styles, { name: 'FlashMessage' });

const titleVariants = {
  large: 'h5',
  medium: 'h6',
  small: 'h6',
};

const messageVariants = {
  large: 'textBig',
  medium: 'textRegular',
  small: 'textSmallCompact',
};

const FlashMessage = ({
  status,
  size,
  title,
  message,
  hideIcon,
  onClose,
  hasButton,
  buttonTitle,
  buttonHandler,
  customIcon,
  viewType = ViewType.plain,
  xpathConfig,
  className,
  onClick,
}: FlashMessageProps) => {
  const classes = useStyles();
  const handleRootClick = useCallback(() => {
    onClick?.();
  }, [onClick]);

  return (
    <div
      className={clsx(classes.root, className, {
        [classes.info]: status === 'info',
        [classes.error]: status === 'error',
        [classes.warning]: status === 'warning',
        [classes.success]: status === 'success',
        [classes.service]: status === 'service',
        [classes.plain]: viewType === ViewType.plain,
        [classes.notification]: viewType === ViewType.notification,
        [classes.alignCenter]: !title || !message,
      })}
      onClick={handleRootClick}
    >
      { !hideIcon && <FlashMessageIcon status={status} customIcon={customIcon} className={classes.icon} /> }
      <div className={classes.content}>
        {title && (
          <Typography
            data-xpath={xpathConfig?.titleBlock}
            className={classes.title}
            variant={get(titleVariants, size || '') || (viewType === ViewType.plain ? 'h5' : 'h6')}
          >
            {title}
          </Typography>
        )}

        {message && (
          <div
            className={clsx({
              [classes.message]: title,
              [classes.plainMessage]: viewType === ViewType.plain,
            })}
            data-xpath={xpathConfig?.messageBlock}
          >
            {typeof message === 'string' && (
              <Typography
                data-xpath={xpathConfig?.titleBlock}
                variant={get(messageVariants, size || '') || 'textSmallCompact'}
              >
                {message}
              </Typography>
            )}
            {typeof message !== 'string' && message}
            {hasButton && (
              <Button
                className={classes.button}
                variant={ButtonVariants.secondary}
                size="medium"
                onClick={buttonHandler}
              >
                {buttonTitle}
              </Button>
            )}
          </div>
        )}
      </div>
      { onClose && (
      <IconButton className={classes.close} onClick={onClose}>
        <CloseIcon />
      </IconButton>
      )}
    </div>
  );
};

export default FlashMessage;
